import { Button, Typography } from '@ag1/ui';

import { useSiteCart } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import { useMoney } from '@portal/shared/hooks/useMoney';
import { IconShippingStars } from '@portal/shared/ui/Icon/picture/IconShippingStars';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_PROGRESS_BAR_VALUE, PROGRESS_BAR_LINK } from '../../constants';
import styles from './SiteCartProgressBar.module.scss';

interface ProgressBarProps {
  value: number;
  max: number;
}
const ProgressBar = ({ value, max }: ProgressBarProps) => {
  const percentage = (value / max) * 100;

  return (
    <div className={styles.progressBarContainer}>
      <div className={styles.progressBar} style={{ width: `${percentage}%` }} />
    </div>
  );
};

export const SiteCartProgressBar = () => {
  const { t } = useTranslation('siteCart');
  const { cart } = useSiteCart();
  const { formatShopifyMoney } = useMoney();

  // TODO: retrieve max
  const max = MAX_PROGRESS_BAR_VALUE;

  const currentValue = Number(cart?.cost.subtotalAmount.amount ?? 0);
  const [reachedGoal, remainingValue] = useMemo(
    () => [currentValue >= max, max - currentValue],
    [currentValue, max]
  );

  if (reachedGoal)
    return (
      <div className={styles.reachedContainer}>
        <Typography variant="portalCaption" size="1" noPadding>
          {t('progressBar.reachedLabel')}
        </Typography>
        <IconShippingStars className={styles.reachedIcon} />
      </div>
    );

  const currencyCode = cart?.cost.subtotalAmount.currencyCode || 'USD';
  const remainingValueObject = { amount: remainingValue.toString(), currencyCode };

  return (
    <div className={styles.container}>
      <ProgressBar value={currentValue} max={max} />
      <div className={styles.info}>
        <Typography variant="caption" size="1" noPadding>
          {t('progressBar.pricePrefix')}
          {formatShopifyMoney(remainingValueObject, { hideDecimalsForWholeNumbers: true })}
          {t('progressBar.priceSuffix')}
        </Typography>
        <Button
          href={PROGRESS_BAR_LINK}
          variant="link"
          size="small"
          noPadding
          className={styles.button}>
          {t('progressBar.buttonLabel')}
        </Button>
      </div>
    </div>
  );
};
